import { useLazyQuery } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { testResolveResponseSchema } from '@eluve/api-contract';
import { SearchCombobox } from '@eluve/blocks';
import {
  Badge,
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Heading,
  Icon,
  Separator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { MedicalNotesLlmOutputTypes } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { SupportedLanguages } from '@eluve/language-utils';
import { MEDICAL_SPECIALTIES } from '@eluve/utils';

import { ScopeBadge } from './components/ScopeBadge';

const uuidSchema = z.string().uuid();

const searchTenantsByNameQuery = graphql(`
  query SearchTenantsByNameForDefaultsTester($search: String!) {
    tenants(
      orderBy: { name: ASC }
      where: {
        _and: [{ deletedAt: { _isNull: true } }, { name: { _ilike: $search } }]
      }
      limit: 50
    ) {
      __typename
      id
      name
    }
  }
`);

const searchTenantsByIdQuery = graphql(`
  query SearchTenantsByIdForDefaultsTester($id: uuid!) {
    tenants(
      orderBy: { name: ASC }
      where: { _and: [{ deletedAt: { _isNull: true } }, { id: { _eq: $id } }] }
      limit: 50
    ) {
      __typename
      id
      name
    }
  }
`);

const defaultsTesterUserFragment = graphql(`
  fragment DefaultsTesterUser on Users @_unmask {
    __typename
    id
    firstName
    lastName
    email
    specialty
  }
`);

const defaultsTesterTenantUserFragment = graphql(
  `
    fragment DefaultsTesterTenantUser on TenantUsers @_unmask {
      __typename
      tenantId
      userId
      user {
        ...DefaultsTesterUser
      }
    }
  `,
  [defaultsTesterUserFragment],
);

const searchTenantUsersByNameQuery = graphql(
  `
    query SearchTenantUsersByNameForDefaultsTester(
      $tenantId: uuid!
      $emailSearch: citext!
      $nameSearch: String!
    ) {
      tenantUsers(
        where: {
          _and: [
            { tenantId: { _eq: $tenantId } }
            {
              _or: [
                { user: { email: { _ilike: $emailSearch } } }
                { user: { firstName: { _ilike: $nameSearch } } }
                { user: { lastName: { _ilike: $nameSearch } } }
              ]
            }
          ]
        }
        limit: 50
      ) {
        ...DefaultsTesterTenantUser
      }
    }
  `,
  [defaultsTesterTenantUserFragment],
);

const searchTenantUsersByIdQuery = graphql(
  `
    query SearchTenantUsersByIdForDefaultsTester(
      $tenantId: uuid!
      $userId: uuid!
    ) {
      tenantUsers(
        where: {
          _and: [{ tenantId: { _eq: $tenantId } }, { userId: { _eq: $userId } }]
        }
        limit: 50
      ) {
        ...DefaultsTesterTenantUser
      }
    }
  `,
  [defaultsTesterTenantUserFragment],
);

interface SelectedTenant {
  id: string;
  name: string;
}

interface SelectedUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  specialty?: string;
}

type TestResult = z.infer<typeof testResolveResponseSchema>;

const languageEntries = Object.entries(SupportedLanguages);

interface TestDefaultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TestDefaultsDialog: React.FC<TestDefaultsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [selectedTenant, setSelectedTenant] = useState<SelectedTenant | null>(
    null,
  );
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [selectedOutputType, setSelectedOutputType] = useState<string | null>(
    null,
  );
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(
    null,
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const apiClient = useApiClient();
  const toast = useToast();

  const [searchTenantsByName] = useLazyQuery(searchTenantsByNameQuery);
  const [searchTenantsById] = useLazyQuery(searchTenantsByIdQuery);
  const [searchUsersByName] = useLazyQuery(searchTenantUsersByNameQuery);
  const [searchUsersById] = useLazyQuery(searchTenantUsersByIdQuery);

  const searchTenants = useCallback(
    async (searchTerm: string): Promise<SelectedTenant[]> => {
      const isUuid = uuidSchema.safeParse(searchTerm).success;
      if (isUuid) {
        const result = await searchTenantsById({
          variables: { id: searchTerm },
        });
        return (
          result.data?.tenants.map((t) => ({ id: t.id, name: t.name })) ?? []
        );
      } else {
        const result = await searchTenantsByName({
          variables: { search: `%${searchTerm}%` },
        });
        return (
          result.data?.tenants.map((t) => ({ id: t.id, name: t.name })) ?? []
        );
      }
    },
    [searchTenantsById, searchTenantsByName],
  );

  const searchUsers = useCallback(
    async (searchTerm: string): Promise<SelectedUser[]> => {
      if (!selectedTenant) return [];

      const isUuid = uuidSchema.safeParse(searchTerm).success;
      const mapTenantUser = (tu: {
        user: {
          id: string;
          email: string;
          firstName: string | null;
          lastName: string | null;
          specialty: string | null;
        } | null;
      }): SelectedUser | null => {
        const user = tu.user;
        if (!user) return null;
        return {
          id: user.id,
          email: user.email,
          firstName: user.firstName ?? undefined,
          lastName: user.lastName ?? undefined,
          specialty: user.specialty ?? undefined,
        };
      };

      if (isUuid) {
        const result = await searchUsersById({
          variables: {
            tenantId: selectedTenant.id,
            userId: searchTerm,
          },
        });
        return (
          result.data?.tenantUsers
            .map(mapTenantUser)
            .filter((u): u is SelectedUser => u !== null) ?? []
        );
      } else {
        const result = await searchUsersByName({
          variables: {
            tenantId: selectedTenant.id,
            emailSearch: `%${searchTerm}%`,
            nameSearch: `%${searchTerm}%`,
          },
        });
        return (
          result.data?.tenantUsers
            .map(mapTenantUser)
            .filter((u): u is SelectedUser => u !== null) ?? []
        );
      }
    },
    [selectedTenant, searchUsersById, searchUsersByName],
  );

  const handleTest = useCallback(async () => {
    if (!selectedTenant || !selectedOutputType) {
      toast.error('Please select a tenant and output type');
      return;
    }

    setIsLoading(true);
    setTestResult(null);

    const response = await apiClient.adminPromptTemplateDefaults.testResolve({
      body: {
        tenantId: selectedTenant.id,
        ...(selectedUser ? { userId: selectedUser.id } : {}),
        llmOutputType: selectedOutputType,
        ...(selectedSpecialty ? { specialty: selectedSpecialty } : {}),
        ...(selectedLanguage ? { language: selectedLanguage } : {}),
      },
    });

    if (response.status === 200) {
      setTestResult(response.body);
    } else {
      toast.error('Failed to test prompt template defaults');
    }

    setIsLoading(false);
  }, [
    selectedTenant,
    selectedUser,
    selectedOutputType,
    selectedSpecialty,
    selectedLanguage,
    apiClient,
    toast,
  ]);

  const handleReset = useCallback(() => {
    setSelectedTenant(null);
    setSelectedUser(null);
    setSelectedOutputType(null);
    setSelectedSpecialty(null);
    setSelectedLanguage(null);
    setTestResult(null);
  }, []);

  const handleDialogOpen = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (!nextOpen) {
        handleReset();
      }
    },
    [handleReset, onOpenChange],
  );

  const handleTenantChange = useCallback((tenant: SelectedTenant | null) => {
    setSelectedTenant(tenant);
    setSelectedOutputType(null);
    setSelectedUser(null);
    setSelectedSpecialty(null);
    setSelectedLanguage(null);
    setTestResult(null);
  }, []);

  const handleOutputTypeChange = useCallback((outputType: string | null) => {
    setSelectedOutputType(outputType);
    setSelectedUser(null);
    setSelectedSpecialty(null);
    setSelectedLanguage(null);
    setTestResult(null);
  }, []);

  const handleUserChange = useCallback((user: SelectedUser | null) => {
    setSelectedUser(user);
    setSelectedSpecialty(null);
    setTestResult(null);
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleDialogOpen}>
      <DialogContent
        size="3xl"
        className="max-h-[90vh] min-h-[60vh] overflow-y-auto"
      >
        <Stack gap={2}>
          <Stack className="grow" gap={2} fullHeight>
            <Stack gap={3} direction="horizontal">
              <Icon name="FlaskConical" size="xl" />
              <DialogHeader>
                <DialogTitle>
                  <Heading size="2xl" color="secondary">
                    Test Prompt Template Defaults
                  </Heading>
                </DialogTitle>
              </DialogHeader>
            </Stack>

            <Separator className="my-2" />

            <Text size="xs" color="secondary">
              Select a tenant and output type, then optionally configure user
              and parameters to test which prompt template default would
              resolve.
            </Text>

            <Stack gap={4}>
              {/* Step 1: Tenant */}
              <Stack gap={2}>
                <Text size="xs" weight="semibold">
                  1. Select Tenant
                </Text>
                <SearchCombobox
                  value={selectedTenant}
                  onValueChange={handleTenantChange}
                  fetchItems={searchTenants}
                  renderItem={(tenant) => (
                    <Stack>
                      <Text size="xs">{tenant.name}</Text>
                      <Text size="xs" color="tertiary">
                        {tenant.id}
                      </Text>
                    </Stack>
                  )}
                  renderSelected={(tenant) => (
                    <Stack>
                      <Text size="xs">{tenant.name}</Text>
                      <Text size="xs" color="tertiary">
                        {tenant.id}
                      </Text>
                    </Stack>
                  )}
                  getSearchValue={(tenant) => `${tenant.name} ${tenant.id}`}
                  getKey={(tenant) => tenant.id}
                  placeholder="Select tenant..."
                  searchPlaceholder="Search by tenant name or ID..."
                  emptyMessage="No tenants found"
                  className="w-full"
                />
              </Stack>

              {/* Step 2: Output Type */}
              <Stack gap={2}>
                <Text size="xs" weight="semibold">
                  2. Select Output Type
                </Text>
                <Combobox disabled={!selectedTenant}>
                  <ComboboxSelectButton className="w-full">
                    {selectedOutputType ?? 'Select output type...'}
                  </ComboboxSelectButton>
                  <ComboboxDropdown searchPlaceholder="Search output types...">
                    {MedicalNotesLlmOutputTypes.map((type) => (
                      <ComboboxOption
                        key={type}
                        onSelect={() => handleOutputTypeChange(type)}
                        searchValue={type}
                      >
                        <Text size="xs">{type}</Text>
                      </ComboboxOption>
                    ))}
                  </ComboboxDropdown>
                </Combobox>
              </Stack>

              {/* Step 3: User (optional) */}
              <Stack gap={2}>
                <Stack gap={1} align="center" direction="horizontal">
                  <Text inline size="xs" weight="semibold">
                    3. User
                  </Text>
                  <Text inline size="xs" color="tertiary">
                    (optional)
                  </Text>
                </Stack>
                <SearchCombobox
                  value={selectedUser}
                  onValueChange={handleUserChange}
                  fetchItems={searchUsers}
                  renderItem={(user) => {
                    const displayName =
                      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                      user.email;
                    return (
                      <Stack>
                        <Text size="xs">{displayName}</Text>
                        <Text size="xs" color="tertiary">
                          {user.email}
                        </Text>
                      </Stack>
                    );
                  }}
                  renderSelected={(user) => {
                    const displayName =
                      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                      user.email;
                    return (
                      <Stack>
                        <Text size="xs">{displayName}</Text>
                        <Text size="xs" color="tertiary">
                          {user.email}
                        </Text>
                      </Stack>
                    );
                  }}
                  getSearchValue={(user) => {
                    const displayName =
                      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                      user.email;
                    return `${displayName} ${user.email} ${user.id}`;
                  }}
                  getKey={(user) => user.id}
                  placeholder="Select user..."
                  searchPlaceholder="Search by email, name, or ID..."
                  emptyMessage="No users found"
                  className="w-full"
                  disabled={!selectedOutputType}
                />
              </Stack>

              {/* Step 4: Specialty (optional) */}
              <Stack gap={2}>
                <Stack gap={1} align="center" direction="horizontal">
                  <Text inline size="xs" weight="semibold">
                    4. Specialty
                  </Text>
                  <Text inline size="xs" color="tertiary">
                    (optional)
                  </Text>
                </Stack>
                {selectedUser ? (
                  <div className="rounded-md border border-border-tertiary px-3 py-2">
                    <Text
                      size="xs"
                      color={selectedUser.specialty ? 'primary' : 'tertiary'}
                    >
                      {selectedUser.specialty ??
                        'No specialty assigned for user'}
                    </Text>
                  </div>
                ) : (
                  <Combobox disabled={!selectedOutputType}>
                    <ComboboxSelectButton className="w-full">
                      {selectedSpecialty ?? 'Any (wildcard)'}
                    </ComboboxSelectButton>
                    <ComboboxDropdown searchPlaceholder="Search specialties...">
                      <ComboboxOption
                        onSelect={() => {
                          setSelectedSpecialty(null);
                          setTestResult(null);
                        }}
                        searchValue="any wildcard"
                      >
                        <Text size="xs" color="tertiary">
                          Any (wildcard)
                        </Text>
                      </ComboboxOption>
                      {MEDICAL_SPECIALTIES.map((spec) => (
                        <ComboboxOption
                          key={spec}
                          onSelect={() => {
                            setSelectedSpecialty(spec);
                            setTestResult(null);
                          }}
                          searchValue={spec}
                        >
                          <Text size="xs">{spec}</Text>
                        </ComboboxOption>
                      ))}
                    </ComboboxDropdown>
                  </Combobox>
                )}
              </Stack>

              {/* Step 5: Language (optional) */}
              <Stack gap={2}>
                <Stack gap={1} align="center" direction="horizontal">
                  <Text inline size="xs" weight="semibold">
                    5. Language
                  </Text>
                  <Text inline size="xs" color="tertiary">
                    (optional)
                  </Text>
                </Stack>
                <Combobox disabled={!selectedOutputType}>
                  <ComboboxSelectButton className="w-full">
                    {selectedLanguage
                      ? (SupportedLanguages[selectedLanguage] ??
                        selectedLanguage)
                      : 'Any (wildcard)'}
                  </ComboboxSelectButton>
                  <ComboboxDropdown searchPlaceholder="Search languages...">
                    <ComboboxOption
                      onSelect={() => {
                        setSelectedLanguage(null);
                        setTestResult(null);
                      }}
                      searchValue="any wildcard"
                    >
                      <Text size="xs" color="tertiary">
                        Any (wildcard)
                      </Text>
                    </ComboboxOption>
                    {languageEntries.map(([code, name]) => (
                      <ComboboxOption
                        key={code}
                        onSelect={() => {
                          setSelectedLanguage(code);
                          setTestResult(null);
                        }}
                        searchValue={`${name} ${code}`}
                      >
                        <Text size="xs">
                          {name} ({code})
                        </Text>
                      </ComboboxOption>
                    ))}
                  </ComboboxDropdown>
                </Combobox>
              </Stack>

              {/* Results Display */}
              {testResult && (
                <>
                  <Separator className="my-2" />
                  <Stack gap={3}>
                    <Text size="xs" weight="semibold">
                      Results
                    </Text>

                    {/* Context */}
                    <Stack gap={4} direction="horizontal">
                      <Stack>
                        <Text inline size="xs" color="tertiary">
                          Tenant
                        </Text>
                        <Text inline size="xs">
                          {testResult.context.tenantName}
                        </Text>
                      </Stack>
                      {testResult.context.userName && (
                        <Stack>
                          <Text inline size="xs" color="tertiary">
                            User
                          </Text>
                          <Text inline size="xs">
                            {testResult.context.userName}
                          </Text>
                        </Stack>
                      )}
                      <Stack>
                        <Text inline size="xs" color="tertiary">
                          Specialty Used
                        </Text>
                        <Text size="xs">
                          {testResult.context.effectiveSpecialty ?? 'Any'}
                        </Text>
                      </Stack>
                    </Stack>

                    {/* Resolved Default */}
                    <Stack gap={1}>
                      <Text size="xs" weight="semibold">
                        Resolved Default
                      </Text>
                      {testResult.resolved ? (
                        <div className="rounded-md border border-border-tertiary p-3">
                          <Stack gap={3} align="center" direction="horizontal">
                            <Icon
                              name="CheckCircle2"
                              className="shrink-0 text-text-positive"
                            />
                            <Stack>
                              <Stack
                                gap={2}
                                align="center"
                                direction="horizontal"
                              >
                                <Text inline size="xs">
                                  {testResult.resolved.promptTemplateName}
                                </Text>
                                <ScopeBadge scope={testResult.resolved.scope} />
                              </Stack>
                              {testResult.resolved.customInstructions && (
                                <Text size="xs" color="tertiary">
                                  Has custom instructions
                                </Text>
                              )}
                            </Stack>
                          </Stack>
                        </div>
                      ) : (
                        <div className="rounded-md border border-border-tertiary p-3">
                          <Stack gap={2} align="center" direction="horizontal">
                            <Icon
                              name="AlertTriangle"
                              className="shrink-0 text-text-warning"
                            />
                            <Text inline size="xs" color="tertiary">
                              No matching default found
                            </Text>
                          </Stack>
                        </div>
                      )}
                    </Stack>

                    {/* All Layers */}
                    <Stack gap={1}>
                      <Text size="xs" weight="semibold">
                        All Applicable Defaults ({testResult.allDefaults.length}
                        )
                      </Text>
                      {testResult.allDefaults.length > 0 ? (
                        <div className="overflow-x-auto rounded-md border border-border-tertiary">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border-tertiary bg-bg-tertiary">
                                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">
                                  Scope
                                </th>
                                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">
                                  Template
                                </th>
                                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">
                                  Specialty
                                </th>
                                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">
                                  Language
                                </th>
                                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">
                                  Custom
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {testResult.allDefaults.map((d) => (
                                <tr
                                  key={d.id}
                                  className="border-b border-border-tertiary last:border-b-0"
                                >
                                  <td className="px-3 py-1.5">
                                    <ScopeBadge scope={d.scope} />
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <Text size="xs">
                                      {d.promptTemplateName}
                                    </Text>
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <Text size="xs" color="secondary">
                                      {d.specialty ?? 'Any'}
                                    </Text>
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <Text size="xs" color="secondary">
                                      {d.language
                                        ? (SupportedLanguages[d.language] ??
                                          d.language)
                                        : 'Any'}
                                    </Text>
                                  </td>
                                  <td className="px-3 py-1.5">
                                    {d.customInstructions ? (
                                      <Badge variant="info">Yes</Badge>
                                    ) : (
                                      <Text size="xs" color="tertiary">
                                        No
                                      </Text>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <Text size="xs" color="tertiary">
                          No defaults configured for this output type
                        </Text>
                      )}
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </Stack>

          <DialogFooter className="w-full">
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
            <Button
              onClick={handleTest}
              disabled={isLoading || !selectedTenant || !selectedOutputType}
            >
              {isLoading ? 'Testing...' : 'Test'}
            </Button>
          </DialogFooter>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
